export type GoogleEnhancedConversionInput = {
  email?: string;
  phone?: string;
  phone_number?: string;
  first_name?: string;
  last_name?: string;
};

export type GoogleEnhancedConversionHashedProperties = Partial<{
  google_ec_email_sha256: string;
  google_ec_phone_number_sha256: string;
  google_ec_first_name_sha256: string;
  google_ec_last_name_sha256: string;
}>;

function normalizeEmail(value?: string): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  return normalized || null;
}

function normalizePhone(value?: string): string | null {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return null;

  return `+${digits}`;
}

function normalizeName(value?: string): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().replace(/\s+/g, " ").toLowerCase();
  return normalized || null;
}

function toHex(digest: ArrayBuffer): string {
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

async function hashSha256(value: string | null): Promise<string | null> {
  if (!value) return null;

  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;

  try {
    const input = new TextEncoder().encode(value);
    const digest = await subtle.digest("SHA-256", input);
    return toHex(digest);
  } catch {
    return null;
  }
}

function stripNullValues<T extends Record<string, string | null>>(
  obj: T
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== null)
  ) as Record<string, string>;
}

export async function buildGoogleEnhancedConversionHashes(
  input: GoogleEnhancedConversionInput
): Promise<GoogleEnhancedConversionHashedProperties> {
  const normalizedEmail = normalizeEmail(input.email);
  const normalizedPhone = normalizePhone(input.phone_number ?? input.phone);
  const normalizedFirstName = normalizeName(input.first_name);
  const normalizedLastName = normalizeName(input.last_name);

  const [emailHash, phoneHash, firstNameHash, lastNameHash] = await Promise.all(
    [
      hashSha256(normalizedEmail),
      hashSha256(normalizedPhone),
      hashSha256(normalizedFirstName),
      hashSha256(normalizedLastName),
    ]
  );

  return stripNullValues({
    google_ec_email_sha256: emailHash,
    google_ec_phone_number_sha256: phoneHash,
    google_ec_first_name_sha256: firstNameHash,
    google_ec_last_name_sha256: lastNameHash,
  }) as GoogleEnhancedConversionHashedProperties;
}
